import React, { useEffect, useRef, useState } from "react";
import { Trash, Plus, UploadSimple, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "../lib/api";

export const OffersAdmin = () => {
  const [offers, setOffers] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/offers").then((r) => setOffers(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  // Comprime la imagen en el navegador antes de subir (evita el límite de tamaño del servidor)
  const compressImage = (f) =>
    new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(f);
      img.onload = () => {
        const maxW = 1080;
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        canvas.toBlob(
          (blob) => resolve(new File([blob], (f.name || "oferta").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" })),
          "image/jpeg",
          0.85
        );
      };
      img.onerror = () => resolve(f);
      img.src = url;
    });

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Debe ser una imagen (JPG o PNG)"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const upload = async () => {
    if (!file) { toast.error("Selecciona una imagen"); return; }
    setUploading(true);
    const optimized = await compressImage(file);
    const fd = new FormData();
    fd.append("file", optimized);
    fd.append("title", title);
    fd.append("active", "true");
    try {
      await api.post("/admin/offers", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Oferta publicada");
      setTitle(""); setFile(null); setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir la imagen"); }
    finally { setUploading(false); }
  };

  const toggleActive = async (o) => {
    const fd = new FormData();
    fd.append("active", (!o.active).toString());
    try {
      await api.put(`/admin/offers/${o.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(!o.active ? "Oferta activada" : "Oferta ocultada");
      load();
    } catch { toast.error("Error al actualizar"); }
  };

  const doDelete = async () => {
    if (!confirm) return;
    try { await api.delete(`/admin/offers/${confirm.id}`); toast.success("Oferta eliminada"); load(); }
    catch { toast.error("Error al eliminar"); }
    setConfirm(null);
  };

  return (
    <div className="space-y-10" data-testid="offers-admin">
      {/* Subir nueva oferta */}
      <div className="border border-white/10 bg-[#111] p-5">
        <h3 className="font-display text-2xl mb-4">Publicar nueva oferta / especial</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="overline block mb-2">Imagen de la promoción (JPG o PNG, máx 8MB)</label>
            <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} className="block w-full text-sm text-gray-300 file:mr-4 file:py-2.5 file:px-4 file:border-0 file:bg-[#D97736] file:text-black file:font-semibold file:uppercase file:tracking-wide file:cursor-pointer" data-testid="offer-file-input" />
            <label className="overline block mb-2 mt-5">Título (opcional)</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej: Happy Hours · MAX a $2" className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white" data-testid="offer-title-input" />
            <button onClick={upload} disabled={uploading || !file} className="mt-6 flex items-center gap-2 bg-[#D97736] hover:bg-[#E88D50] disabled:opacity-50 text-black font-semibold px-5 py-3 uppercase tracking-wide text-sm" data-testid="offer-upload-btn">
              <UploadSimple size={18} weight="bold" /> {uploading ? "Subiendo..." : "Publicar oferta"}
            </button>
          </div>
          <div className="flex items-center justify-center border border-dashed border-white/15 min-h-[200px] p-3">
            {preview ? (
              <img src={preview} alt="Vista previa" className="max-h-72 w-auto object-contain" data-testid="offer-preview" />
            ) : (
              <span className="text-gray-600 text-sm">Vista previa de la imagen</span>
            )}
          </div>
        </div>
      </div>

      {/* Ofertas publicadas */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-3xl">Ofertas publicadas <span className="text-gray-500 text-lg">({offers.length})</span></h3>
        </div>
        {offers.length === 0 ? (
          <div className="border border-white/10 bg-[#111] p-10 text-center text-gray-500 text-sm">Aún no has publicado ofertas. Sube una imagen arriba.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {offers.map((o) => (
              <div key={o.id} className={`border bg-[#111] overflow-hidden ${o.active ? "border-[#D97736]/50" : "border-white/10 opacity-60"}`} data-testid={`offer-row-${o.id}`}>
                <img src={`${BACKEND_URL}${o.image_url}`} alt={o.title} className="w-full h-56 object-contain bg-black" />
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{o.title || "Sin título"}</span>
                    <span className={`text-xs uppercase tracking-wide px-2 py-1 ${o.active ? "text-green-400 border border-green-500/40" : "text-gray-500 border border-white/10"}`}>
                      {o.active ? "Activa" : "Oculta"}
                    </span>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => toggleActive(o)} className="flex-1 flex items-center justify-center gap-2 border border-white/15 hover:border-[#D97736] hover:text-[#D97736] py-2.5 text-sm uppercase tracking-wide" data-testid={`toggle-offer-${o.id}`}>
                      {o.active ? <><EyeSlash size={16} /> Ocultar</> : <><Eye size={16} /> Activar</>}
                    </button>
                    <button onClick={() => setConfirm(o)} className="w-11 flex items-center justify-center border border-white/15 hover:border-red-500 hover:text-red-400" data-testid={`del-offer-${o.id}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#141414] border border-white/15 p-6 max-w-sm w-full" data-testid="offer-delete-confirm">
            <h3 className="font-display text-3xl">¿Eliminar oferta?</h3>
            <p className="text-gray-400 text-sm mt-2">Se quitará de la página. Esta acción no se puede deshacer.</p>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setConfirm(null)} className="px-5 py-3 border border-white/20 uppercase tracking-wide text-sm">Cancelar</button>
              <button onClick={doDelete} className="px-5 py-3 bg-red-600 hover:bg-red-500 text-white uppercase tracking-wide text-sm transition-colors" data-testid="confirm-offer-delete">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
